import type {
  Movie,
  Cinema,
  Showtime,
  Screen,
  User,
  WatchlistEntry,
  Notification,
  CinemaReview,
  PaginatedResponse,
} from '@cinemake/shared';

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// Auth token for the API. Set by AuthSync (Clerk) — attached as
// `Authorization: Bearer` on every request when present.
let authToken: string | null = null;
let tokenProvider: (() => Promise<string | null>) | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
}

export function setAuthTokenProvider(provider: (() => Promise<string | null>) | null) {
  tokenProvider = provider;
}

async function resolveToken(): Promise<string | null> {
  if (tokenProvider) {
    try {
      return await tokenProvider();
    } catch {
      return authToken;
    }
  }
  return authToken;
}

const BASE_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

/**
 * Coerce a value that the server may have returned as either an array or a
 * JSON-encoded string into a plain array. CinemaKE persists list fields like
 * `amenities` and `genres` as strings in Prisma, so the list endpoints
 * (`/cinemas`, `/cinemas/:id`, `/movies/now-playing`, ...) echo them back
 * as strings. The detail endpoints sometimes normalise them; the list ones
 * do not. We accept both shapes here so the web client gets arrays either
 * way. Unknown / non-stringifiable values collapse to an empty array.
 */
export function coerceStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value as string[];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as string[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function coerceObjectArray<T = Record<string, unknown>>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

interface RequestOptions {
  signal?: AbortSignal;
  params?: Record<string, string | number | boolean | undefined | null>;
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
}

function buildQuery(params?: RequestOptions['params']): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.append(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = `${BASE_URL}${path}${buildQuery(opts.params)}`;
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = await resolveToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const hasBody = opts.body !== undefined && opts.body !== null;
  if (hasBody) headers['Content-Type'] = 'application/json';
  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    signal: opts.signal,
    headers,
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      detail = body.error || body.message || detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(detail || `Request failed: ${res.status}`, res.status);
  }
  const json = (await res.json()) as { success: boolean; data: T; error?: string; message?: string };
  if (!json.success) {
    throw new ApiError(json.error || json.message || 'Request failed', res.status);
  }
  return json.data;
}

async function requestPaged<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<{ items: T[]; pagination: PaginatedResponse<T>['pagination'] | undefined }> {
  const url = `${BASE_URL}${path}${buildQuery(opts.params)}`;
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = await resolveToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const hasBody = opts.body !== undefined && opts.body !== null;
  if (hasBody) headers['Content-Type'] = 'application/json';
  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    signal: opts.signal,
    headers,
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      detail = body.error || body.message || detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(detail || `Request failed: ${res.status}`, res.status);
  }
  const json = (await res.json()) as PaginatedResponse<T>;
  if (!json.success) {
    throw new ApiError(json.error || json.message || 'Request failed', res.status);
  }
  return { items: json.data, pagination: json.pagination };
}

export const api = {
  // Movies
  nowPlaying(params?: { page?: number; limit?: number; local?: boolean }) {
    return requestPaged<Movie>('/movies/now-playing', { params });
  },
  upcoming(params?: { page?: number; limit?: number }) {
    return requestPaged<Movie>('/movies/upcoming', { params });
  },
  movie(id: string) {
    return request<Movie & { showtimes?: Showtime[] }>(
      `/movies/${encodeURIComponent(id)}`,
    );
  },
  searchMovies(q: string, params?: { page?: number; limit?: number }) {
    return requestPaged<Movie>('/movies/search', { params: { q, ...params } });
  },

  // Cinemas
  cinemas(params?: { city?: string }) {
    return request<Cinema[]>('/cinemas', { params });
  },
  cinema(id: string) {
    return request<Cinema & { screens: Screen[]; showtimes?: Showtime[] }>(
      `/cinemas/${encodeURIComponent(id)}`,
    );
  },

  // Showtimes
  showtimes(params: {
    movieId?: string;
    cinemaId?: string;
    date?: string;
    tzOffset?: number;
  }) {
    return request<Showtime[]>('/showtimes', { params });
  },

  // User (authenticated)
  user: {
    getProfile() {
      return request<User>('/user/profile');
    },
    updateProfile(data: { city?: string }) {
      return request<User>('/user/profile', { method: 'PATCH', body: data });
    },
    updateSettings(data: { notificationsEnabled?: boolean }) {
      return request<User>('/user/settings', { method: 'PATCH', body: data });
    },
    getNotifications() {
      return request<Notification[]>('/user/notifications');
    },
    addNotification(movieId: string) {
      return request<Notification>(`/user/notify/${encodeURIComponent(movieId)}`, {
        method: 'POST',
        body: { type: 'RELEASE' },
      });
    },
    deleteNotification(movieId: string) {
      return request<null>(`/user/notify/${encodeURIComponent(movieId)}`, { method: 'DELETE' });
    },
    getWatchlist() {
      return request<WatchlistEntry[]>('/user/watchlist');
    },
    addToWatchlist(movieId: string) {
      return request<WatchlistEntry>(`/user/watchlist/${encodeURIComponent(movieId)}`, {
        method: 'POST',
      });
    },
    removeFromWatchlist(movieId: string) {
      return request<null>(`/user/watchlist/${encodeURIComponent(movieId)}`, { method: 'DELETE' });
    },
    /** Permanently deletes the signed-in account (Clerk + all owned data). */
    deleteAccount() {
      return request<{ deleted: boolean }>(`/user/account`, { method: 'DELETE' });
    },
  },

  // Cinema reviews (authenticated mutations)
  addCinemaReview(cinemaId: string, data: { rating: number; body?: string }) {
    return request<CinemaReview>(`/cinemas/${encodeURIComponent(cinemaId)}/reviews`, {
      method: 'POST',
      body: data,
    });
  },
  removeCinemaReview(cinemaId: string) {
    return request<null>(`/cinemas/${encodeURIComponent(cinemaId)}/reviews`, { method: 'DELETE' });
  },
};

export type MovieList = Awaited<ReturnType<typeof api.nowPlaying>>;
